import { Box, Typography } from '@mui/material'
import { displayPriceFunction } from 'components/Common/DisplayValues/DisplayPrice'

export interface ModalDetailsProp {
  price: number
  currency: string
  displayVat: boolean
  currentBooking?: any
  isFlexibleOption: boolean
  additionalOptions?: number | 'displayOnly'
}

const pricingBreakdownExcludeVat =
  'Price may also exclude tolls, congestion/low emission zone charges, parking, airport pick-up/drop-off charges, waiting time, additional stops'

const pricingbreakdownIncludeVat =
  'Pricing may be subject to waiting time, airport drop off/collection fees, parking, tolls, clean air charges and may be subject to congestion zone charges'

const ModalDetails = ({
  price,
  currency,
  displayVat,
  isFlexibleOption,
  currentBooking,
  additionalOptions,
}: ModalDetailsProp) => {
  const currentPriceDisplay = displayPriceFunction(
    currentBooking?.jobs?.[0]?.estimatedPrice,
    displayVat,
  )
  const currentPrice = Number(currentPriceDisplay?.exactPrice ?? 0)

  const newTotalPrice =
    price + (typeof additionalOptions === 'number' ? additionalOptions : 0)
  const priceDifference = newTotalPrice - currentPrice
  const isIncrease = priceDifference > 0
  const isDecrease = priceDifference < 0

  return (
    <>
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #D5D5D5',
          padding: '20px 16px 0 16px',
          backgroundColor: '#FDFDFD',
          borderRadius: '4px 4px 0 0',
          marginTop: '32px',
        }}
      >
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '600',
              fontSize: '17px',
              lineHeight: '21px',
            }}
          >
            Pricing Breakdown
          </Typography>
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '400',
              fontSize: '14px',
              lineHeight: '21px',
              marginTop: '8px',
            }}
          >
            {displayVat
              ? pricingbreakdownIncludeVat
              : pricingBreakdownExcludeVat}
          </Typography>
        </Box>

        {/* Current Price Row */}
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
            marginBottom: '12px',
          }}
        >
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '400',
              fontSize: '16px',
              lineHeight: '24px',
            }}
          >
            Current Price
          </Typography>
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '400',
              fontSize: '14px',
              lineHeight: '21px',
              marginTop: '8px',
            }}
          >
            {`Est. ${currency}${currentPrice.toFixed(2)}`}
          </Typography>
        </Box>

        {/* New Price Row */}
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '12px',
          }}
        >
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '400',
              fontSize: '16px',
              lineHeight: '24px',
            }}
          >
            New Price
          </Typography>
          <Typography
            component="p"
            sx={{
              color: '#414042',
              fontWeight: '400',
              fontSize: '14px',
              lineHeight: '21px',
              marginTop: '8px',
            }}
          >
            {`Est. ${currency}${newTotalPrice.toFixed(2)}`}
          </Typography>
        </Box>

        {/* Additional Options Row (if applicable) */}
        {isFlexibleOption || typeof additionalOptions !== 'number' ? (
          <Box
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '24px',
            }}
          >
            <Typography
              component="p"
              sx={{
                color: '#414042',
                fontWeight: '400',
                fontSize: '16px',
                lineHeight: '24px',
              }}
            >
              Additional Options
            </Typography>
            <Typography
              component="p"
              sx={{
                color: '#414042',
                fontWeight: '400',
                fontSize: '14px',
                lineHeight: '21px',
                marginTop: '8px',
              }}
            >
              {`${currency}${
                typeof additionalOptions === 'number'
                  ? additionalOptions.toFixed(2)
                  : '0.00'
              }`}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <Box
        component="div"
        sx={{
          display: 'flex',
          border: '1px solid #D5D5D5',
          padding: '18px 16px',
          backgroundColor: isIncrease
            ? '#FFF3CD'
            : isDecrease
            ? '#D4EDDA'
            : '#FDFDFD',
          justifyContent: 'space-between',
          borderTop: 0,
          borderRadius: '0 0 4px 4px',
        }}
      >
        <Typography
          component="p"
          sx={{
            color: '#414042',
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '24px',
          }}
        >
          Price Change
        </Typography>
        <Typography
          component="p"
          sx={{
            color: isIncrease ? '#856404' : isDecrease ? '#155724' : '#414042',
            fontWeight: '600',
            fontSize: '16px',
            lineHeight: '21px',
          }}
        >
          {priceDifference === 0
            ? 'No change'
            : `${isIncrease ? '+' : ''}${currency}${Math.abs(
                priceDifference,
              ).toFixed(2)} ${isIncrease ? 'increase' : 'decrease'}`}
        </Typography>
      </Box>
    </>
  )
}

export default ModalDetails
